/* Copyright (c) 2017 Backpack Health LLC */

import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { injectIntl } from 'react-intl';
import * as _ from 'lodash';

import ProfileSetupController from './ProfileSetup.controller';
import { getLoggedInUser, getActiveProfile, getActiveProfileId, getMainProfileId } from '../../redux/reduxSelectors';
import ProfileActions from '../../redux/ProfileActions';
import { actions as consentActions } from '../../../redux/ducks/consent';
import { actions as systemNotificationActions } from '../../../redux/ducks/systemNotification';
import ProfilePhrases from '../../../../phraseKeys/Profile.phrase';
import { actions as userActions } from '../../../redux/ducks/user';
import { selectors as actionItemSelectors } from '../../../redux/ducks/actionItems';
import { selectors as listSelectors } from '../../../redux/ducks/lists/lists';
import { actions as systemModalActions } from '../../../redux/ducks/systemModal';
import RouteNames from '../../RouteNames';
import { selectors as ownerSelectors } from '../../../redux/ducks/owners';
import { selectors as groupSelectors } from '../../../redux/ducks/group';
import NavigationActions from '../../redux/NavigationActions';
import { queryCache } from 'react-query';
import { useUIState } from '../../../hooks/useUIState';

function mapStateToProps(state, routerProps) {
  const profile = getActiveProfile(state);
  const profileId = getActiveProfileId(state);
  const isMainProfile = profileId === getMainProfileId(state);
  const genderCollection = listSelectors.getGenderCollection(state);

  const activeOwnerProfiles = ownerSelectors.getActiveOwnersForProfile(profile.data.id, state);

  const genderIdMap = {};
  genderCollection.forEach((g) => {
    genderIdMap[g.id] = g;
  });

  const user = getLoggedInUser(state);

  const defaultFinishUrl = _.get(routerProps, 'location.state.destinationUrl', '') || RouteNames.profileInfo();
  const finishUrl = user.hasJoinGroupIntent() ? RouteNames.intendedGroup() : defaultFinishUrl;

  return {
    user,
    isNewProfile: routerProps.location.pathname === RouteNames.newProfileSetup(),
    finishUrl,
    hasSoleAccess: activeOwnerProfiles.length === 1,
    profile,
    profileId,
    isMainProfile,
    phraseData: ProfilePhrases.myProfile(isMainProfile, profile.greetingName()),
    profileActionItems: actionItemSelectors.getProfileActionItems(state, profile.data.id),
    userActionItems: actionItemSelectors.getUserActionItems(state, user.data.id),
    groupIds: groupSelectors.getGroupIdsForProfile(state, profileId),
    genderIdMap,
    submitProfileChanges: (profileData) => {
      ProfileActions.api.updateProfile(profileData);
    },
    requestActiveProfile: (callback) => {
      ProfileActions.api.getProfile(profile.data.id, callback);
    },
    goToHome: () => {
      NavigationActions.replace(routerProps.history, RouteNames.myBackpack());
    },
    isResidenceStateRequired: (countryId) => {
      return listSelectors.isResidenceStateRequired(countryId, state);
    },
  };
}

function mapDispatchToProps(dispatch) {
  const r = bindActionCreators({
    showNotification: systemNotificationActions.showNotification,
    dismissNotification: systemNotificationActions.dismissNotification,
    showHintNotification: systemNotificationActions.showHintNotification,
  }, dispatch);

  return {
    ...r,
    clearIntent: (userData, cb) => {
      dispatch(userActions.submitUserUpdate(
        {
          ...userData,
          intent: null,
        },
        cb,
      ));
    },
    refreshActionItems: (userId, profileId) => {
      return new Promise((res) => {
        queryCache.setQueryData(['profileActionItems', profileId], {
          data: { steps: [] },
          isLoaded: true,
        });
        queryCache.setQueryData(['userActionItems', userId], {
          data: { steps: [] },
          isLoaded: true,
        });
        queryCache.invalidateQueries('userActionItems');
        queryCache.invalidateQueries('profileActionItems');
        queryCache.invalidateQueries('profile');
        res();
      })
    },
    showSystemModal: (content) => {
      dispatch(systemModalActions.show(content));
    },
    requestConsents: (profileId) => {
      dispatch(consentActions.requestAllConsents(profileId));
    },
  };
}

const TempProfileSetup = (props) => {
  const [, uiActions] = useUIState();
  return (
    <ProfileSetupController
      {...props}
      switchToMainProfile={() => {
        uiActions.switchToMainProfile();
      }}
    />
  );
}

export default connect(mapStateToProps, mapDispatchToProps)(injectIntl(TempProfileSetup));
